import { useState } from 'react';
import { toast } from 'sonner';
import { X, Bug, Sparkles, ClipboardList, BookOpen, ExternalLink } from 'lucide-react';
import { SelectedElement } from '../../hooks/useTicketLogger';
import { logTicket, LogTicketPayload } from '../../services/ticketService';

interface Props {
  selected: SelectedElement;
  screenshotBase64: string | null;
  reportedBy: string;
  onClose: () => void;
}

const TYPES: { value: LogTicketPayload['type']; label: string; icon: React.ReactNode }[] = [
  { value: 'Bug', label: 'Bug', icon: <Bug size={14} /> },
  { value: 'Feature', label: 'Feature', icon: <Sparkles size={14} /> },
  { value: 'Task', label: 'Task', icon: <ClipboardList size={14} /> },
  { value: 'User Story', label: 'User Story', icon: <BookOpen size={14} /> },
];

const PRIORITIES: { value: LogTicketPayload['priority']; label: string }[] = [
  { value: 1, label: '1 — Critical' },
  { value: 2, label: '2 — High' },
  { value: 3, label: '3 — Medium' },
  { value: 4, label: '4 — Low' },
];

export function TicketLoggerModal({ selected, screenshotBase64, reportedBy, onClose }: Props) {
  const [title, setTitle] = useState('');
  const [type, setType] = useState<LogTicketPayload['type']>('Bug');
  const [priority, setPriority] = useState<LogTicketPayload['priority']>(3);
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) { setError('Title is required'); return; }
    setSubmitting(true);
    setError('');
    try {
      const result = await logTicket({
        title: title.trim(),
        type,
        priority,
        description: description.trim() || undefined,
        pageUrl: window.location.href,
        elementLabel: selected.label,
        screenshotBase64: screenshotBase64 || undefined,
        reportedBy,
      });
      toast.success(`ADO #${result.adoId} created`, {
        description: title,
        action: { label: 'Open', onClick: () => window.open(result.adoUrl, '_blank') },
        duration: 8000,
      });
      onClose();
    } catch {
      setError('Failed to create ticket. Try again.');
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Backdrop */}
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(5,40,49,0.6)', backdropFilter: 'blur(2px)' }} />

      {/* Modal */}
      <div style={{
        position: 'relative',
        background: '#fff',
        borderRadius: 16,
        width: '100%',
        maxWidth: 520,
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 24px 64px rgba(0,0,0,0.25)',
        margin: '0 16px',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #e2e8f0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'rgba(0,199,196,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#00c7c4' }}>
              <Bug size={16} />
            </div>
            <div>
              <p style={{ fontSize: 15, fontWeight: 700, color: '#052831', margin: 0 }}>Log ticket</p>
              <p style={{ fontSize: 11, color: '#64748b', margin: 0 }}>ADO · Spectrum</p>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 4, display: 'flex', borderRadius: 6 }}>
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Error */}
          {error && (
            <div style={{ padding: '10px 20px', background: '#fef2f2', borderBottom: '1px solid #fee2e2', color: '#b91c1c', fontSize: 13 }}>
              {error}
            </div>
          )}

          <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
            {/* Context */}
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 12px', fontSize: 12, color: '#64748b' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                <ExternalLink size={11} />
                <span style={{ wordBreak: 'break-all' }}>{window.location.pathname}</span>
              </div>
              <div style={{ color: '#94a3b8' }}>Component: <span style={{ color: '#52525b' }}>{selected.label}</span></div>
            </div>

            {/* Screenshot thumbnail */}
            {screenshotBase64 && (
              <div>
                <p style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Screenshot</p>
                <img
                  src={`data:image/png;base64,${screenshotBase64}`}
                  alt="Selected element"
                  style={{ maxHeight: 140, maxWidth: '100%', borderRadius: 8, border: '1px solid #e2e8f0', objectFit: 'contain', display: 'block' }}
                />
              </div>
            )}

            {/* Type */}
            <div>
              <label style={{ fontSize: 13, fontWeight: 600, color: '#052831', display: 'block', marginBottom: 6 }}>Type</label>
              <div style={{ display: 'flex', borderRadius: 10, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                {TYPES.map(t => (
                  <button
                    key={t.value}
                    type="button"
                    onClick={() => setType(t.value)}
                    style={{
                      flex: 1,
                      padding: '8px 4px',
                      fontSize: 12,
                      fontWeight: 600,
                      border: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 4,
                      background: type === t.value ? '#00c7c4' : '#fff',
                      color: type === t.value ? '#fff' : '#64748b',
                      transition: 'all 0.15s',
                    }}
                  >
                    {t.icon}{t.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Title */}
            <div>
              <label style={{ fontSize: 13, fontWeight: 600, color: '#052831', display: 'block', marginBottom: 6 }}>
                Title <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <input
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder={selected.label}
                autoFocus
                style={{
                  width: '100%',
                  border: '1px solid #e2e8f0',
                  borderRadius: 10,
                  padding: '8px 12px',
                  fontSize: 13,
                  color: '#052831',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            {/* Priority */}
            <div>
              <label style={{ fontSize: 13, fontWeight: 600, color: '#052831', display: 'block', marginBottom: 6 }}>Priority</label>
              <select
                value={priority}
                onChange={e => setPriority(Number(e.target.value) as LogTicketPayload['priority'])}
                style={{
                  width: '100%',
                  border: '1px solid #e2e8f0',
                  borderRadius: 10,
                  padding: '8px 12px',
                  fontSize: 13,
                  color: '#052831',
                  background: '#fff',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              >
                {PRIORITIES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>

            {/* Description */}
            <div>
              <label style={{ fontSize: 13, fontWeight: 600, color: '#052831', display: 'block', marginBottom: 6 }}>Description <span style={{ color: '#94a3b8', fontWeight: 400 }}>(optional)</span></label>
              <textarea
                value={description}
                onChange={e => setDescription(e.target.value)}
                rows={3}
                placeholder="Steps to reproduce, expected vs actual, context…"
                style={{
                  width: '100%',
                  border: '1px solid #e2e8f0',
                  borderRadius: 10,
                  padding: '8px 12px',
                  fontSize: 13,
                  color: '#052831',
                  outline: 'none',
                  resize: 'vertical',
                  boxSizing: 'border-box',
                }}
              />
            </div>
          </div>

          {/* Footer */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '12px 20px', borderTop: '1px solid #e2e8f0', background: '#f8fafc' }}>
            <button
              type="button"
              onClick={onClose}
              style={{ padding: '8px 16px', borderRadius: 10, border: '1px solid #e2e8f0', background: '#fff', color: '#64748b', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              style={{
                padding: '8px 20px',
                borderRadius: 10,
                border: 'none',
                background: submitting ? '#94a3b8' : '#00c7c4',
                color: '#fff',
                fontSize: 13,
                fontWeight: 700,
                cursor: submitting ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              {submitting ? 'Creating…' : 'Log ticket'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
